/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApplicantSearchSettingsRequest } from '../models/ApplicantSearchSettingsRequest';
import type { ApplicantSearchSettingsResponse } from '../models/ApplicantSearchSettingsResponse';
import type { CompletedStage } from '../models/CompletedStage';
import type { DetailedApplicant } from '../models/DetailedApplicant';
import type { DuplicateApplicantSettingsRejectionReasonsResponse } from '../models/DuplicateApplicantSettingsRejectionReasonsResponse';
import type { DuplicateApplicantSettingsRequest } from '../models/DuplicateApplicantSettingsRequest';
import type { DuplicateApplicantSettingsResponse } from '../models/DuplicateApplicantSettingsResponse';
import type { DuplicateApplicantSettingsUpdateResponse } from '../models/DuplicateApplicantSettingsUpdateResponse';
import type { EmailMessageContent } from '../models/EmailMessageContent';
import type { Emails } from '../models/Emails';
import type { HiringGoal } from '../models/HiringGoal';
import type { HiringGoalBulkCreateUpdateResponse } from '../models/HiringGoalBulkCreateUpdateResponse';
import type { HiringGoalOwnerOptions } from '../models/HiringGoalOwnerOptions';
import type { HiringGoalPositionRequest } from '../models/HiringGoalPositionRequest';
import type { LocationGroup } from '../models/LocationGroup';
import type { Opening } from '../models/Opening';
import type { OpeningsAttributeFilter } from '../models/OpeningsAttributeFilter';
import type { OpeningsTableSavedView } from '../models/OpeningsTableSavedView';
import type { OpeningsTableSavedViewRequest } from '../models/OpeningsTableSavedViewRequest';
import type { OpeningsTableSavedViewsResponse } from '../models/OpeningsTableSavedViewsResponse';
import type { OrderedHiringGoal } from '../models/OrderedHiringGoal';
import type { Pagination } from '../models/Pagination';
import type { SeatActionResult } from '../models/SeatActionResult';
import type { SeatAvailability } from '../models/SeatAvailability';
import type { SeatIdCheck } from '../models/SeatIdCheck';
import type { SeatIdReservation } from '../models/SeatIdReservation';
import type { SeatIdReservationRequest } from '../models/SeatIdReservationRequest';
import type { SeatList } from '../models/SeatList';
import type { UpdateApplicantRequest } from '../models/UpdateApplicantRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ApiSelfServeV2Service {

    /**
     * show applicant search settings
     * Returns the applicant search settings for the account
     * @returns ApplicantSearchSettingsResponse successful
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantSearchSettings(): CancelablePromise<ApplicantSearchSettingsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicant_search_settings',
        });
    }

    /**
     * update applicant search settings
     * Updates the applicant search settings for the account
     * @param requestBody
     * @returns any successful update
     * @throws ApiError
     */
    public static patchApiSelfServeV2ApplicantSearchSettings(
        requestBody?: ApplicantSearchSettingsRequest,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api_self_serve/v2/applicant_search_settings',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * index
     * Returns list of email conversations
     * @param externalId Applicant's external_id
     * @returns Emails success
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantsEmailConversations(
        externalId: string,
    ): CancelablePromise<Emails> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants/{external_id}/email_conversations',
            path: {
                'external_id': externalId,
            },
        });
    }

    /**
     * email_content
     * Returns email content
     * @param externalId Applicant's external_id
     * @param emailMessageExternalId EmailMessage external_id
     * @returns EmailMessageContent success
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantsEmailMessagesEmailContent(
        externalId: string,
        emailMessageExternalId: string,
    ): CancelablePromise<EmailMessageContent> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants/{external_id}/email_messages/{email_message_external_id}/email_content',
            path: {
                'external_id': externalId,
                'email_message_external_id': emailMessageExternalId,
            },
        });
    }

    /**
     * Get completed stages for an applicant
     * Returns all completed stages for a specific applicant, including sub-stages for multi-stages
     * @param externalId External ID for the applicant
     * @param includeCurrentStage Whether to include the current stage
     * @param scopeToCurrentOpening When true, only return stages in the applicant's current opening workflow (for print stage picker)
     * @returns any successful
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantsTransitionsCompletedStages(
        externalId: string,
        includeCurrentStage?: boolean,
        scopeToCurrentOpening?: boolean,
    ): CancelablePromise<{
        /**
         * Array of completed stages sorted by completion date (earliest first)
         */
        completed_stages: Array<CompletedStage>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants/{external_id}/transitions/completed_stages',
            path: {
                'external_id': externalId,
            },
            query: {
                'include_current_stage': includeCurrentStage,
                'scope_to_current_opening': scopeToCurrentOpening,
            },
        });
    }

    /**
     * List applicants
     * @param c
     * @param query
     * @returns void
     * @throws ApiError
     */
    public static listApiSelfServeV2Applicants(
        c: Array<string>,
        query?: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants',
            query: {
                'query': query,
                'c[]': c,
            },
            errors: {
                400: `Search failed`,
            },
        });
    }

    /**
     * Retrieve Applicant
     * Returns the detailed applicant serialization.
     * @param externalId External ID for the applicant
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2Applicants(
        externalId: string,
    ): CancelablePromise<{
        applicant?: DetailedApplicant;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants/{external_id}',
            path: {
                'external_id': externalId,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not found`,
            },
        });
    }

    /**
     * Update applicant
     * Updates an applicant's profile attributes, subscriptions, and (optionally)
     * granular communication consent. Any of the four `consent_*` keys present in
     * the payload are routed through Applicant::RecordConsent — one consent_*
     * column write + applicant_consent_logs audit row + can_receive_sms mirror
     * (for sms_transactional) per call, all in a single transaction.
     * @param externalId External ID for the applicant
     * @param requestBody
     * @returns any success
     * @throws ApiError
     */
    public static putApiSelfServeV2Applicants(
        externalId: string,
        requestBody?: UpdateApplicantRequest,
    ): CancelablePromise<{
        applicant?: DetailedApplicant;
    }> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api_self_serve/v2/applicants/{external_id}',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                404: `Not found`,
            },
        });
    }

    /**
     * resubmit_partner_data
     * Re-sends partner data for a specified applicant
     * @param externalId External ID for the applicant
     * @param requestBody Partner information
     * @returns any success
     * @throws ApiError
     */
    public static postApiSelfServeV2ApplicantsResubmitPartnerData(
        externalId: string,
        requestBody: {
            partner_id?: string;
            partner_stage_id?: string;
        },
    ): CancelablePromise<{
        message?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/applicants/{external_id}/resubmit_partner_data',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                403: `Unauthorized`,
            },
        });
    }

    /**
     * index
     * Returns Hiring Goals
     * @param openingId filter query for opening_id
     * @param page pagination setting for page to view
     * @param perPage pagination setting for max number of records to return
     * @param open filter query for open scope
     * @param active filter query for active scope
     * @param pending filter query for pending scope
     * @param scheduled filter query for scheduled scope
     * @param closed filter query for closed scope
     * @param current filter query for current scope
     * @param past filter query for past scope
     * @param sortBy filter query for sort_by scope
     * @param sortDirection filter query for sort_direction scope
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2BulkHiringGoals(
        openingId: string,
        page: string,
        perPage: string,
        open?: string,
        active?: string,
        pending?: string,
        scheduled?: string,
        closed?: string,
        current?: string,
        past?: string,
        sortBy?: string,
        sortDirection?: string,
    ): CancelablePromise<{
        hiring_goals: Array<HiringGoal>;
        metadata?: {
            goal: number;
            hires_count: number;
            count_by_status: {
                active: number;
                pending: number;
                scheduled: number;
                closed: number;
            };
        };
        /**
         * Pagination info (empty object when no results)
         */
        pagination: {
            current_page?: number | null;
            next_page?: number | null;
            per_page?: number | null;
            total_pages?: number | null;
            total_count?: number | null;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/bulk_hiring_goals',
            query: {
                'opening_id': openingId,
                'open': open,
                'active': active,
                'pending': pending,
                'scheduled': scheduled,
                'closed': closed,
                'current': current,
                'past': past,
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
            },
        });
    }

    /**
     * create_or_update
     * Bulk Creates or Updates Hiring Goals
     * @param requestBody Hiring Goals
     * @returns HiringGoalBulkCreateUpdateResponse success
     * @throws ApiError
     */
    public static postApiSelfServeV2BulkHiringGoalsCreateOrUpdate(
        requestBody: {
            goals: Array<{
                goal: number;
                hire_by?: string;
                start_date?: string;
                automatically_close_on_date?: string;
                funnel_id?: string;
                external_id?: string;
                /**
                 * Req ID under requisition hiring goals. Omit to preserve; blank clears.
                 */
                req_id?: string | null;
                /**
                 * Account member external ID under requisition hiring goals. Omit to preserve; null clears.
                 */
                owner_id?: string | null;
                form_index?: number;
                seats?: Array<{
                    seat_external_id: string;
                    /**
                     * Saved seat external ID on an edit; omit for a new seat.
                     */
                    external_id?: string;
                }>;
                /**
                 * Every goal-visible attribute type. A blank value clears that type on the goal.
                 */
                customer_attributes?: Array<{
                    type: string;
                    value?: string;
                }>;
            }>;
            funnel_id: string;
        },
    ): CancelablePromise<HiringGoalBulkCreateUpdateResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/bulk_hiring_goals/create_or_update',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                422: `Hiring goal refused`,
            },
        });
    }

    /**
     * destroy
     * Closes or Soft Deletes a Hiring Goal
     * @param externalId Hiring Goal External ID
     * @returns void
     * @throws ApiError
     */
    public static deleteApiSelfServeV2BulkHiringGoals(
        externalId: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api_self_serve/v2/bulk_hiring_goals/{external_id}',
            path: {
                'external_id': externalId,
            },
            errors: {
                401: `unauthorized`,
                404: `Hiring goal not found`,
                422: `Hiring goal refused`,
            },
        });
    }

    /**
     * index
     * Returns a list of data keys or full data key objects if get_data_key_objects is true
     * @param getDataKeyWithOptions If true, returns full data key objects instead of just keys
     * @param getDataKeyObjects If true, returns full data key objects instead of just keys
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2DataKeys(
        getDataKeyWithOptions?: boolean,
        getDataKeyObjects?: boolean,
    ): CancelablePromise<{
        data_keys: Array<(string | {
            id?: number;
            key?: string;
            created_at?: string;
            concept_id?: string | null;
            key_type?: string;
            restricted?: boolean;
            storage?: string;
            title?: string;
        } | {
            id?: number;
            key?: string;
            created_at?: string;
            concept_id?: string | null;
            key_type?: string;
            restricted?: boolean;
            storage?: string;
            title?: string;
            possible_values?: Array<string>;
        })>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/data_keys',
            query: {
                'get_data_key_with_options': getDataKeyWithOptions,
                'get_data_key_objects': getDataKeyObjects,
            },
        });
    }

    /**
     * update
     * Updates a specific data key
     * @param id Data key's ID
     * @param requestBody
     * @returns any success
     * @throws ApiError
     */
    public static putApiSelfServeV2DataKeys(
        id: number,
        requestBody?: {
            concept_id?: string | null;
            concept_option_mappings?: Record<string, string>;
        },
    ): CancelablePromise<{
        message: string;
        data_key: {
            id?: number;
            key?: string;
            concept_id?: string | null;
        };
    }> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api_self_serve/v2/data_keys/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * show duplicate applicant settings
     * Returns the duplicate applicant detection settings for the account
     * @returns DuplicateApplicantSettingsResponse successful
     * @throws ApiError
     */
    public static getApiSelfServeV2DuplicateApplicantSettings(): CancelablePromise<DuplicateApplicantSettingsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/duplicate_applicant_settings',
        });
    }

    /**
     * update duplicate applicant settings
     * Updates the duplicate applicant detection settings for the account
     * @param requestBody
     * @returns DuplicateApplicantSettingsUpdateResponse successful update
     * @throws ApiError
     */
    public static patchApiSelfServeV2DuplicateApplicantSettings(
        requestBody?: DuplicateApplicantSettingsRequest,
    ): CancelablePromise<DuplicateApplicantSettingsUpdateResponse> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api_self_serve/v2/duplicate_applicant_settings',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * list rejection reasons available for duplicate handling
     * Returns the rejection reasons the account can apply to duplicate applicants
     * @returns DuplicateApplicantSettingsRejectionReasonsResponse successful
     * @throws ApiError
     */
    public static getApiSelfServeV2DuplicateApplicantSettingsRejectionReasons(): CancelablePromise<DuplicateApplicantSettingsRejectionReasonsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/duplicate_applicant_settings/rejection_reasons',
        });
    }

    /**
     * List hiring goal owner options
     * @param query Case-insensitive name search
     * @returns HiringGoalOwnerOptions Owner options
     * @throws ApiError
     */
    public static getApiSelfServeV2HiringGoalOwnerOptions(
        query?: string,
    ): CancelablePromise<HiringGoalOwnerOptions> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/hiring_goal_owner_options',
            query: {
                'query': query,
            },
            errors: {
                401: `Manage hiring goals required`,
                404: `Requisition hiring goals disabled`,
            },
        });
    }

    /**
     * Reorder an active hiring goal
     * @param externalId
     * @param requestBody
     * @returns any Active goals in stored order
     * @throws ApiError
     */
    public static patchApiSelfServeV2HiringGoalPositions(
        externalId: string,
        requestBody: HiringGoalPositionRequest,
    ): CancelablePromise<{
        hiring_goals: Array<OrderedHiringGoal>;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api_self_serve/v2/hiring_goal_positions/{external_id}',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Manage hiring goals required`,
                403: `Opening management required`,
                404: `Goal unavailable or requisition hiring goals disabled`,
                409: `Order changed or lock unavailable; reread before retrying`,
                422: `Invalid position or order shape`,
            },
        });
    }

    /**
     * index
     * Returns location groups
     * @param query Filter location groups returned by matching title
     * @param userId Filters by user accessible funnels (requires enterprise=true)
     * @param includeLocations When true, each location group includes its locations
     * @returns any includes locations when include_locations=true
     * @throws ApiError
     */
    public static getApiSelfServeV2LocationGroups(
        query?: string,
        userId?: string,
        includeLocations?: boolean,
    ): CancelablePromise<{
        location_groups: Array<LocationGroup>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/location_groups',
            query: {
                'query': query,
                'user_id': userId,
                'include_locations': includeLocations,
            },
        });
    }

    /**
     * show
     * Returns a location group
     * @param id Location group's external_id
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2LocationGroups1(
        id: string,
    ): CancelablePromise<{
        location_group: LocationGroup;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/location_groups/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * bulk_delete_preview
     * Splits a bulk opening selection into openings that can be deleted (no applicants) and ones blocked by applicants, so the confirmation modal can list each group. Read-only; the counts are exact and the named lists are capped.
     * @param requestBody
     * @returns any success
     * @throws ApiError
     */
    public static postApiSelfServeV2OpeningsBulkDeletePreview(
        requestBody: {
            select_all?: boolean;
            funnel_external_ids?: Array<string>;
            excluded_external_ids?: Array<string>;
            query?: string;
            active?: Array<boolean>;
            approval_status?: Array<string>;
            listing_status?: Array<'public' | 'internal' | 'not_posted' | 'intake_closed'>;
            status_filters?: string;
            user_openings?: boolean;
            sourcing_purchase_type?: string;
            location?: string;
            position_id?: string;
            attribute_type_id?: string;
            owner_ids?: Array<string>;
            req_ids?: Array<string>;
            hiring_goal_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            brand_ids?: Array<string>;
            location_ids?: Array<string>;
            workflow_ids?: Array<string>;
            opening_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            position_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            location_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            location_group_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
        },
    ): CancelablePromise<{
        deletable_count: number;
        non_deletable_count: number;
        deletable: Array<{
            funnel_external_id: string;
            name: string;
        }>;
        non_deletable: Array<{
            funnel_external_id: string;
            name: string;
            applicant_count: number;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/openings/bulk_delete_preview',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * bulk_delete
     * Enqueues a background job to delete every opening in the selection that has no applicants. Returns 202 Accepted immediately.
     * @param requestBody
     * @returns any accepted
     * @throws ApiError
     */
    public static postApiSelfServeV2OpeningsBulkDelete(
        requestBody: {
            select_all?: boolean;
            funnel_external_ids?: Array<string>;
            excluded_external_ids?: Array<string>;
            query?: string;
            active?: Array<boolean>;
            approval_status?: Array<string>;
            listing_status?: Array<'public' | 'internal' | 'not_posted' | 'intake_closed'>;
            status_filters?: string;
            user_openings?: boolean;
            sourcing_purchase_type?: string;
            location?: string;
            position_id?: string;
            attribute_type_id?: string;
            owner_ids?: Array<string>;
            req_ids?: Array<string>;
            hiring_goal_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            brand_ids?: Array<string>;
            location_ids?: Array<string>;
            workflow_ids?: Array<string>;
            opening_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            position_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            location_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
            location_group_attributes?: Array<{
                attribute_key?: string;
                attribute_values?: Array<string>;
                missing?: boolean;
            }>;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/openings/bulk_delete',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * bulk_listing_status_preview
     * Previews a bulk sourcing change on four-state accounts with Bulk edit permission. Counts cover visible openings; skipped names require manage permission and are capped at 200. Pending Approval skips every target; inactive openings skip Public and Internal.
     * @param requestBody
     * @returns any success
     * @throws ApiError
     */
    public static postApiSelfServeV2OpeningsBulkListingStatusPreview(
        requestBody: {
            target_listing_status: 'public' | 'internal' | 'not_posted' | 'intake_closed';
            select_all?: boolean;
            funnel_external_ids?: Array<string>;
            excluded_external_ids?: Array<string>;
            query?: string;
            active?: Array<boolean>;
            approval_status?: Array<string>;
            listing_status?: Array<'public' | 'internal' | 'not_posted' | 'intake_closed'>;
            status_filters?: string;
            user_openings?: boolean;
            sourcing_purchase_type?: string;
            location?: string;
            location_id?: string;
            position_id?: string;
            attribute_type_id?: string;
            brand_id?: string;
            workflow_id?: string;
            owner_ids?: Array<string>;
            req_ids?: Array<string>;
            brand_ids?: Array<string>;
            location_ids?: Array<string>;
            workflow_ids?: Array<string>;
            opening_attributes?: Array<OpeningsAttributeFilter>;
            hiring_goal_attributes?: Array<OpeningsAttributeFilter>;
            position_attributes?: Array<OpeningsAttributeFilter>;
            location_attributes?: Array<OpeningsAttributeFilter>;
            location_group_attributes?: Array<OpeningsAttributeFilter>;
        },
    ): CancelablePromise<{
        statuses: Array<{
            listing_status: 'public' | 'internal' | 'not_posted' | 'intake_closed';
            count: number;
            /**
             * False means No change at the target status.
             */
            changes: boolean;
        }>;
        changeable_count: number;
        skipped_count: number;
        skipped: Array<{
            funnel_external_id: string;
            name: string;
            reason: 'inactive' | 'pending_approval';
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/openings/bulk_listing_status_preview',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `invalid target or unavailable sourcing model`,
            },
        });
    }

    /**
     * bulk_listing_status
     * Enqueues a sourcing status change on four-state accounts with Bulk edit permission. The worker rechecks manage permission and saved eligibility for each opening. Internal requires the internal career site. Returns 202 Accepted.
     * @param requestBody
     * @returns any accepted
     * @throws ApiError
     */
    public static postApiSelfServeV2OpeningsBulkListingStatus(
        requestBody: {
            target_listing_status: 'public' | 'internal' | 'not_posted' | 'intake_closed';
            select_all?: boolean;
            funnel_external_ids?: Array<string>;
            excluded_external_ids?: Array<string>;
            query?: string;
            active?: Array<boolean>;
            approval_status?: Array<string>;
            listing_status?: Array<'public' | 'internal' | 'not_posted' | 'intake_closed'>;
            status_filters?: string;
            user_openings?: boolean;
            sourcing_purchase_type?: string;
            location?: string;
            location_id?: string;
            position_id?: string;
            attribute_type_id?: string;
            brand_id?: string;
            workflow_id?: string;
            owner_ids?: Array<string>;
            req_ids?: Array<string>;
            brand_ids?: Array<string>;
            location_ids?: Array<string>;
            workflow_ids?: Array<string>;
            opening_attributes?: Array<OpeningsAttributeFilter>;
            hiring_goal_attributes?: Array<OpeningsAttributeFilter>;
            position_attributes?: Array<OpeningsAttributeFilter>;
            location_attributes?: Array<OpeningsAttributeFilter>;
            location_group_attributes?: Array<OpeningsAttributeFilter>;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/openings/bulk_listing_status',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `invalid target or unavailable sourcing model`,
            },
        });
    }

    /**
     * bulk_status_preview
     * Returns the active/inactive breakdown for a bulk opening status selection, so the confirmation modal can show how many openings would actually change.
     * @param requestBody
     * @returns any success
     * @throws ApiError
     */
    public static postApiSelfServeV2OpeningsBulkStatusPreview(
        requestBody: {
            target_status: 'active' | 'inactive';
            select_all?: boolean;
            funnel_external_ids?: Array<string>;
            excluded_external_ids?: Array<string>;
            query?: string;
            active?: Array<boolean>;
            approval_status?: Array<string>;
            listing_status?: Array<'public' | 'internal' | 'not_posted' | 'intake_closed'>;
            status_filters?: string;
            user_openings?: boolean;
            sourcing_purchase_type?: string;
            location?: string;
            position_id?: string;
            attribute_type_id?: string;
            owner_ids?: Array<string>;
            req_ids?: Array<string>;
            hiring_goal_attributes?: Array<OpeningsAttributeFilter>;
            brand_ids?: Array<string>;
            location_ids?: Array<string>;
            workflow_ids?: Array<string>;
        },
    ): CancelablePromise<{
        active_count: number;
        inactive_count: number;
        changeable_count: number;
        total_count: number;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/openings/bulk_status_preview',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `invalid target_status`,
            },
        });
    }

    /**
     * bulk_status
     * Enqueues a background job to change the status (active/inactive) of every opening in the selection. Returns 202 Accepted immediately.
     * @param requestBody
     * @returns any accepted
     * @throws ApiError
     */
    public static postApiSelfServeV2OpeningsBulkStatus(
        requestBody: {
            target_status: 'active' | 'inactive';
            select_all?: boolean;
            funnel_external_ids?: Array<string>;
            excluded_external_ids?: Array<string>;
            query?: string;
            active?: Array<boolean>;
            approval_status?: Array<string>;
            listing_status?: Array<'public' | 'internal' | 'not_posted' | 'intake_closed'>;
            status_filters?: string;
            user_openings?: boolean;
            sourcing_purchase_type?: string;
            location?: string;
            position_id?: string;
            attribute_type_id?: string;
            owner_ids?: Array<string>;
            req_ids?: Array<string>;
            hiring_goal_attributes?: Array<OpeningsAttributeFilter>;
            brand_ids?: Array<string>;
            location_ids?: Array<string>;
            workflow_ids?: Array<string>;
        },
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/openings/bulk_status',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `invalid target_status`,
            },
        });
    }

    /**
     * index
     * Returns Openings and Pagination
     * @param workflowId Determines which workflows to return openings from
     * @param title Filter openings returned by matching title
     * @param page Determines which page to return
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param active Determines whether to return active or inactive openings
     * @param query Matches opening, location, position and brand names, and open goal Req IDs when requisition hiring goals are enabled. Every search word must match.
     * @param locationIds filters openings with given location ids
     * @param positionIds filters openings with given position ids
     * @param brandIds filters openings with given brand ids
     * @param isSelectedForSourcing filters openings which is selected for sourcing dashboard
     * @param statusFilters JSON-encoded array of { active, approval_status } pairs, OR'd together. Only takes effect when the account has openings-table-customize-columns enabled - otherwise ignored, falling back to active/approval_status. Lets multiple statuses be combined without cross-producting their active/approval_status values.
     * @param ownerIds Filters by the opening owner, or location owner when unset. Also matches active or scheduled goal owners when requisition hiring goals are enabled.
     * @param workflowIds Filters openings with given workflow ids
     * @param openingAttributes Filters openings by customer attributes attached to the opening itself. Encoded as opening_attributes[][attribute_key]=K&opening_attributes[][attribute_values][]=v&opening_attributes[][missing]=false - see OpeningsAttributeFilter. Combined with the other three *_attributes[] params, and across multiple entries within one param, via AND (each entry is a distinct attribute type); a single entry's own attribute_values (plus missing) are OR'd together.
     * @param positionAttributes Filters openings by customer attributes attached to the opening's position. Same encoding and combining rules as opening_attributes[] - see OpeningsAttributeFilter.
     * @param locationAttributes Filters openings by customer attributes attached to the opening's location. Same encoding and combining rules as opening_attributes[] - see OpeningsAttributeFilter.
     * @param locationGroupAttributes Filters openings by customer attributes attached to the opening's location group. Same encoding and combining rules as opening_attributes[] - see OpeningsAttributeFilter.
     * @param reqIds Matches exact open goal Req IDs case-insensitively. Ignored unless requisition hiring goals are enabled.
     * @param hiringGoalAttributes Matches attributes on active or scheduled goals. Uses the opening_attributes encoding. Requires requisition hiring goals and openings-table-customize-columns.
     * @param listingStatus Matches any of the given sourcing statuses. Ignored unless the account uses the four-state sourcing model.
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2Openings(
        workflowId?: string,
        title?: string,
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        active?: boolean,
        query?: string,
        locationIds?: Array<string>,
        positionIds?: Array<string>,
        brandIds?: Array<string>,
        isSelectedForSourcing?: boolean,
        statusFilters?: string,
        ownerIds?: Array<string>,
        workflowIds?: Array<string>,
        openingAttributes?: Array<OpeningsAttributeFilter>,
        positionAttributes?: Array<OpeningsAttributeFilter>,
        locationAttributes?: Array<OpeningsAttributeFilter>,
        locationGroupAttributes?: Array<OpeningsAttributeFilter>,
        reqIds?: Array<string>,
        hiringGoalAttributes?: Array<OpeningsAttributeFilter>,
        listingStatus?: Array<'public' | 'internal' | 'not_posted' | 'intake_closed'>,
    ): CancelablePromise<{
        openings: Array<Opening>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings',
            query: {
                'workflow_id': workflowId,
                'title': title,
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'active': active,
                'query': query,
                'location_ids[]': locationIds,
                'position_ids[]': positionIds,
                'brand_ids[]': brandIds,
                'is_selected_for_sourcing': isSelectedForSourcing,
                'status_filters': statusFilters,
                'owner_ids[]': ownerIds,
                'workflow_ids[]': workflowIds,
                'opening_attributes[]': openingAttributes,
                'position_attributes[]': positionAttributes,
                'location_attributes[]': locationAttributes,
                'location_group_attributes[]': locationGroupAttributes,
                'req_ids[]': reqIds,
                'hiring_goal_attributes[]': hiringGoalAttributes,
                'listing_status[]': listingStatus,
            },
        });
    }

    /**
     * owners
     * Returns paginated effective opening owners among the user's accessible openings, including active or scheduled goal owners when requisition hiring goals are enabled.
     * @param query Filters owners by matching name
     * @param page Determines which page to return
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2OpeningsOwners(
        query?: string,
        page?: number,
    ): CancelablePromise<{
        owners: Array<{
            /**
             * Owner's User#external_id
             */
            id: string;
            name: string;
        }>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings/owners',
            query: {
                'query': query,
                'page': page,
            },
        });
    }

    /**
     * brands
     * Returns a paginated list of the distinct brands (via the funnel's location) among the current user's accessible openings - powers the Brand filter's option list. Locations without a brand are excluded. Some accounts have hundreds of brands, so results are offset-paginated rather than returned in one payload.
     * @param query Filters brands by matching name
     * @param page Determines which page to return
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2OpeningsBrands(
        query?: string,
        page?: number,
    ): CancelablePromise<{
        brands: Array<{
            /**
             * Brand's Accounts::Brand#external_id
             */
            id: string;
            name: string;
        }>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings/brands',
            query: {
                'query': query,
                'page': page,
            },
        });
    }

    /**
     * workflows
     * Returns a paginated list of the distinct workflows among the current user's accessible openings - powers the Workflow filter's option list. Only returns results for accounts with the workflow-system feature flag enabled; otherwise returns an empty list, since such accounts cannot search by workflow at all.
     * @param query Filters workflows by matching title
     * @param page Determines which page to return
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2OpeningsWorkflows(
        query?: string,
        page?: number,
    ): CancelablePromise<{
        workflows: Array<{
            /**
             * Workflow's Workflow#external_id
             */
            id: string;
            name: string;
        }>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings/workflows',
            query: {
                'query': query,
                'page': page,
            },
        });
    }

    /**
     * location_groups
     * Returns a paginated, flat list of distinct locations (via the funnel's location), each carrying its location group's id/name, among the current user's accessible openings - powers the Location filter's option list. The frontend re-nests locations under their group client-side. Some accounts have thousands of locations, so results are offset-paginated rather than returned in one payload.
     * @param query Filters locations by matching group name or location name
     * @param page Determines which page to return
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2OpeningsLocationGroups(
        query?: string,
        page?: number,
    ): CancelablePromise<{
        locations: Array<{
            /**
             * Location#external_id
             */
            id: string;
            name: string;
            /**
             * LocationGroup#external_id
             */
            location_group_id: string;
            location_group_name: string;
        }>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings/location_groups',
            query: {
                'query': query,
                'page': page,
            },
        });
    }

    /**
     * attribute_values
     * Returns the full, unpaginated list of values for a given customer attribute type - powers the value sub-panel of the Attributes filter. Values are not paginated, matching the convention used elsewhere for a single attribute type's values (see CustomerAttributesPack::Public::AttributeTypeSerializer). Only available when the openings-table-customize-columns feature flag is enabled.
     * @param attributeKey CustomerAttributes::AttributeType#key - the same identifier used in the index action's *_attributes[][attribute_key] filter params, and unique per account.
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2OpeningsAttributeValues(
        attributeKey: string,
    ): CancelablePromise<{
        attribute_values: Array<{
            id: number;
            value: string;
            attribute_type_id: number;
            value_type: string;
            created_at?: string;
            updated_at?: string;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings/attribute_values',
            query: {
                'attribute_key': attributeKey,
            },
            errors: {
                403: `forbidden`,
            },
        });
    }

    /**
     * show
     * Returns a single opening JSON representation
     * @param externalId Opening#external_id
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2Openings1(
        externalId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings/{external_id}',
            path: {
                'external_id': externalId,
            },
        });
    }

    /**
     * update
     * Updates an opening
     * @param id Opening#external_id
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static putApiSelfServeV2Openings(
        id: string,
        requestBody?: {
            /**
             * Opening title
             */
            title?: string;
            /**
             * Whether the opening is active
             */
            active?: boolean;
            /**
             * Additional information about the opening
             */
            additional_info?: string;
            /**
             * Application deadline
             */
            application_deadline?: string;
            /**
             * Compensation amount
             */
            compensation?: string;
            /**
             * Currency code for compensation
             */
            compensation_currency_code?: string;
            /**
             * Type of compensation
             */
            compensation_type?: string;
            /**
             * Minimum compensation
             */
            compensation_min?: number;
            /**
             * Maximum compensation
             */
            compensation_max?: number;
            /**
             * Custom identifier
             */
            custom_id?: string;
            /**
             * Description of the opening
             */
            description?: string;
            /**
             * Force showing compensation
             */
            force_show_compensation?: boolean;
            /**
             * External title
             */
            external_title?: string;
            /**
             * Location external ID
             */
            location_id?: string;
            /**
             * Position external ID
             */
            position_id?: string;
            /**
             * Owner external ID
             */
            owner_id?: string;
            /**
             * Workflow external ID
             */
            workflow_id?: string;
            /**
             * Shift external ID
             */
            shift_id?: string;
            /**
             * Type of job
             */
            job_type?: string;
            /**
             * Job hours
             */
            job_hours?: string;
            /**
             * Job industry
             */
            job_industry?: string;
            /**
             * Whether the opening is private
             */
            is_private?: boolean;
            /**
             * Whether this is a hiring funnel
             */
            is_hiring_funnel?: boolean;
            /**
             * Whether this is a sourcing funnel
             */
            is_sourcing_funnel?: boolean;
            /**
             * Whether this is an internal funnel
             */
            is_internal_funnel?: boolean;
            /**
             * Internal job duration in days
             */
            internal_job_duration?: number;
            /**
             * Internal job expiration date
             */
            internal_job_expiration_date?: string;
            /**
             * Action to take on internal job expiration
             */
            internal_job_expiration_action?: string;
        },
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api_self_serve/v2/openings/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `unauthorized`,
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * list saved views
     * Returns the current user's saved Openings-table filter views
     * @returns OpeningsTableSavedViewsResponse success
     * @throws ApiError
     */
    public static getApiSelfServeV2OpeningsTableSavedViews(): CancelablePromise<OpeningsTableSavedViewsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings_table_saved_views',
            errors: {
                403: `forbidden when customize-columns flag is disabled`,
            },
        });
    }

    /**
     * create a saved view
     * Saves the given Openings-table filter selection under a name for the current user
     * @param requestBody
     * @returns OpeningsTableSavedView success
     * @throws ApiError
     */
    public static postApiSelfServeV2OpeningsTableSavedViews(
        requestBody?: OpeningsTableSavedViewRequest,
    ): CancelablePromise<OpeningsTableSavedView> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/openings_table_saved_views',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `bad request when the root parameter is missing`,
                403: `forbidden when feature flags are disabled`,
                422: `validation errors (e.g. duplicate title)`,
            },
        });
    }

    /**
     * update a saved view
     * Overwrites one of the current user's saved views with a new title and/or filter selection
     * @param id The saved view's external ID
     * @param requestBody
     * @returns OpeningsTableSavedView success
     * @throws ApiError
     */
    public static patchApiSelfServeV2OpeningsTableSavedViews(
        id: string,
        requestBody: OpeningsTableSavedViewRequest,
    ): CancelablePromise<OpeningsTableSavedView> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api_self_serve/v2/openings_table_saved_views/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `bad request when the root parameter is missing`,
                403: `forbidden when feature flags are disabled`,
                404: `not found for another user's saved view`,
                422: `validation errors (e.g. duplicate title)`,
            },
        });
    }

    /**
     * delete a saved view
     * Deletes one of the current user's saved views
     * @param id The saved view's external ID
     * @returns void
     * @throws ApiError
     */
    public static deleteApiSelfServeV2OpeningsTableSavedViews(
        id: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api_self_serve/v2/openings_table_saved_views/{id}',
            path: {
                'id': id,
            },
            errors: {
                403: `forbidden when feature flags are disabled`,
                404: `not found for another user's saved view`,
            },
        });
    }

    /**
     * Check seat availability
     * Read-only, unlocked availability check for up to 500 applicants. Applicants outside the caller scope are omitted.
     * @param requestBody
     * @returns SeatAvailability Seat capacity and refusal details
     * @throws ApiError
     */
    public static postApiSelfServeV2SeatAvailability(
        requestBody: {
            funnel_id: string;
            stage_id: string;
            applicant_ids: Array<string>;
        },
    ): CancelablePromise<SeatAvailability> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/seat_availability',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `Opening or stage unavailable`,
                422: `Invalid applicant selection`,
            },
        });
    }

    /**
     * Reserve seat IDs
     * Seat contract; this endpoint returns 404 until seat ID reservations are enabled.
     * @param requestBody
     * @returns SeatIdReservation Reserved seat IDs
     * @throws ApiError
     */
    public static postApiSelfServeV2SeatIdReservations(
        requestBody: SeatIdReservationRequest,
    ): CancelablePromise<SeatIdReservation> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/seat_id_reservations',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `Seats unavailable`,
                422: `Count must be an integer between 1 and 500`,
            },
        });
    }

    /**
     * Check a Seat ID
     * Read-only advisory check. Save revalidates the ID. No seats are created or IDs reserved.
     * @param seatExternalId
     * @param hiringGoalId The edited goal; its live seats do not conflict. Canceled Seat IDs stay spent.
     * @param openingId The opening being edited, including for a new goal. Its account owns the Seat ID namespace.
     * @returns SeatIdCheck Seat ID availability or validation refusal
     * @throws ApiError
     */
    public static getApiSelfServeV2SeatsCheck(
        seatExternalId: string,
        hiringGoalId?: string,
        openingId?: string,
    ): CancelablePromise<SeatIdCheck> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/seats/check',
            query: {
                'seat_external_id': seatExternalId,
                'hiring_goal_id': hiringGoalId,
                'opening_id': openingId,
            },
            errors: {
                400: `Malformed query`,
                403: `Manage hiring goals permission required`,
                404: `Seats unavailable or goal not accessible`,
            },
        });
    }

    /**
     * List seats by hiring goal
     * Seat contract; this endpoint returns 404 until seat reads are enabled.
     * @param openingId
     * @returns SeatList Seats in goal order
     * @throws ApiError
     */
    public static getApiSelfServeV2Seats(
        openingId: string,
    ): CancelablePromise<SeatList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/seats',
            query: {
                'opening_id': openingId,
            },
            errors: {
                404: `Seats unavailable`,
            },
        });
    }

    /**
     * Cancel an open seat
     * Seat contract; this endpoint returns 404 until seat cancellation is enabled.
     * @param externalId
     * @returns SeatActionResult Cancelled seat and hiring goal
     * @throws ApiError
     */
    public static postApiSelfServeV2SeatsCancel(
        externalId: string,
    ): CancelablePromise<SeatActionResult> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/seats/{external_id}/cancel',
            path: {
                'external_id': externalId,
            },
            errors: {
                403: `Manage hiring goals permission required`,
                404: `Seats unavailable`,
                422: `Seat cancellation refused`,
            },
        });
    }

    /**
     * Restore a cancelled seat
     * Seat contract; this endpoint returns 404 until seat restoration is enabled.
     * @param externalId
     * @returns SeatActionResult Restored seat and hiring goal
     * @throws ApiError
     */
    public static postApiSelfServeV2SeatsRestore(
        externalId: string,
    ): CancelablePromise<SeatActionResult> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/seats/{external_id}/restore',
            path: {
                'external_id': externalId,
            },
            errors: {
                403: `Manage hiring goals permission required`,
                404: `Seats unavailable`,
                422: `Seat restoration refused`,
            },
        });
    }

    /**
     * feature flag values for user
     * Returns an object with feature flag values for the specified user.
     * @param id User's external_id
     * @returns any successful
     * @throws ApiError
     */
    public static getApiSelfServeV2UsersFeatures(
        id: string,
    ): CancelablePromise<{
        features: any;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/users/{id}/features',
            path: {
                'id': id,
            },
        });
    }

    /**
     * active presence users
     * Given the user ids from a Pusher presence roster, returns the ones that still
     * belong to a live user, so the caller can drop members whose accounts were deleted.
     *
     * Results are scoped to the authenticated account and its parent account. Non-numeric
     * and duplicate ids are dropped, and only the first 500 are considered — anything
     * beyond that is absent from both lists, so a caller can tell "deleted" apart from
     * "not checked".
     *
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static postApiSelfServeV2PusherActiveUsers(
        requestBody: {
            /**
             * User ids from a Pusher presence roster.
             */
            user_ids: Array<number>;
        },
    ): CancelablePromise<{
        /**
         * The requested ids this response covers.
         */
        checked_user_ids: Array<number>;
        /**
         * Checked ids still belonging to a live user.
         */
        active_user_ids: Array<number>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/pusher/active_users',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}